import { LinearGradient } from 'expo-linear-gradient';
import React, { useEffect, useState } from 'react';
import { Pressable, View, Text, StyleSheet, ActivityIndicator } from 'react-native';
import { supabase } from '../../lib/supabase';
import { useTheme, makeThemedStyles } from '../../v10/theme';
import type { OrderWidgetProps } from './types';

interface ProtocolWithSteps {
  id: string;
  title: string;
  steps: Array<{
    id: string;
    title: string;
    done: boolean | null;
    order?: number | null;
  }>;
}

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function getTodayDayName(): string {
  return DAYS_OF_WEEK[new Date().getDay()];
}

export function ThemisWidget({ userId, gear, data, onOpen, onEngage }: OrderWidgetProps) {
  const { t } = useTheme();
  const styles = useStyles();
  const [protocol, setProtocol] = useState<ProtocolWithSteps | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    const loadTodaysProtocol = async () => {
      try {
        const todayDay = getTodayDayName();

        // Fetch all schedules for this user and filter for today
        const { data: schedules, error: schedErr } = await supabase
          .from('protocol_schedules')
          .select('protocol_id, instances')
          .eq('user_id', userId);

        if (schedErr) throw schedErr;

        // Find a protocol scheduled for today
        let todaysProtocolId: string | null = null;
        for (const schedule of schedules ?? []) {
          const instances = (schedule.instances as Array<{ days: string[] }>) ?? [];
          if (instances.some((inst) => inst.days.includes(todayDay))) {
            todaysProtocolId = schedule.protocol_id;
            break;
          }
        }

        if (!todaysProtocolId) {
          setProtocol(null);
          setLoading(false);
          return;
        }

        // Fetch protocol and its steps
        const [protRes, stepsRes] = await Promise.all([
          supabase
            .from('protocols')
            .select('id, title')
            .eq('id', todaysProtocolId)
            .eq('user_id', userId)
            .maybeSingle(),
          supabase
            .from('protocol_items')
            .select('id, title, done, order')
            .eq('protocol_id', todaysProtocolId)
            .eq('user_id', userId)
            .order('order', { ascending: true }),
        ]);

        if (protRes.error) throw protRes.error;
        if (stepsRes.error) throw stepsRes.error;

        if (protRes.data) {
          setProtocol({
            id: protRes.data.id,
            title: protRes.data.title,
            steps: (stepsRes.data ?? []) as ProtocolWithSteps['steps'],
          });
        }
      } catch (e) {
        console.error('[ThemisWidget] Load failed:', e);
        setError(e instanceof Error ? e.message : 'Failed to load protocol');
      } finally {
        setLoading(false);
      }
    };

    void loadTodaysProtocol();
  }, [userId]);

  const handleToggleStep = async (stepId: string, currentDone: boolean | null) => {
    if (!userId || !protocol) return;

    const nextDone = !currentDone;

    // Optimistic update
    setProtocol((prev) =>
      prev
        ? {
            ...prev,
            steps: prev.steps.map((s) => (s.id === stepId ? { ...s, done: nextDone } : s)),
          }
        : prev
    );

    // Persist
    const { error: err } = await supabase
      .from('protocol_items')
      .update({ done: nextDone })
      .eq('id', stepId)
      .eq('user_id', userId);

    if (!err) {
      // Running a protocol step is THEMIS's engagement for the day (green dot).
      onEngage?.();
    }

    if (err) {
      console.error('[ThemisWidget] Toggle failed:', err);
      // Revert on error
      setProtocol((prev) =>
        prev
          ? {
              ...prev,
              steps: prev.steps.map((s) => (s.id === stepId ? { ...s, done: currentDone } : s)),
            }
          : prev
      );
    }
  };

  const handleAddStep = async () => {
    if (!userId || !protocol) return;

    // Calculate next order using max of actual order field (matching ThemisTool behavior)
    const nextOrder = protocol.steps.length > 0
      ? Math.max(...protocol.steps.map((s) => s.order ?? 0)) + 1
      : 0;
    const newTitle = 'New step';

    const { data: newStepData, error: err } = await supabase
      .from('protocol_items')
      .insert({
        user_id: userId,
        protocol_id: protocol.id,
        title: newTitle,
        done: false,
        duration_minutes: 1,
        order: nextOrder,
        created_at: new Date().toISOString(),
      })
      .select('id, title, done, order')
      .single();

    if (err) {
      console.error('[ThemisWidget] Add step failed:', err);
      return;
    }

    if (newStepData) {
      setProtocol((prev) =>
        prev
          ? {
              ...prev,
              steps: [...prev.steps, { id: newStepData.id, title: newStepData.title, done: newStepData.done, order: newStepData.order }],
            }
          : prev
      );
    }
  };

  if (loading) {
    return (
      <View style={styles.container}>
<LinearGradient colors={t.panelGradient} start={{ x: 0.2, y: 0 }} end={{ x: 0.8, y: 1 }} style={StyleSheet.absoluteFill} pointerEvents="none" />
        <ActivityIndicator color={t.gold} size="small" />
      </View>
    );
  }

  if (!protocol) {
    return (
      <View style={styles.container}>
<LinearGradient colors={t.panelGradient} start={{ x: 0.2, y: 0 }} end={{ x: 0.8, y: 1 }} style={StyleSheet.absoluteFill} pointerEvents="none" />
        <Text style={styles.emptyText}>No protocol today</Text>
        <Pressable style={styles.openPill} onPress={onOpen}>
          <Text style={styles.openPillText}>Open THEMIS</Text>
        </Pressable>
      </View>
    );
  }

  const displaySteps = protocol.steps.slice(0, 6);
  const hasMoreSteps = protocol.steps.length > 6;

  return (
    <View style={styles.container}>
<LinearGradient colors={t.panelGradient} start={{ x: 0.2, y: 0 }} end={{ x: 0.8, y: 1 }} style={StyleSheet.absoluteFill} pointerEvents="none" />
      {/* Header */}
      <Pressable style={styles.header} onPress={onOpen}>
        <Text style={styles.headerText}>{protocol.title.toUpperCase()} — TODAY</Text>
      </Pressable>

      {/* Step rows */}
      {displaySteps.map((step, idx) => (
        <View key={step.id} style={[styles.stepRow, idx < displaySteps.length - 1 && styles.stepRowBorder]}>
          <Pressable
            style={styles.checkbox}
            onPress={() => handleToggleStep(step.id, step.done)}
            hitSlop={8}
          >
            <View style={[styles.checkboxInner, step.done && styles.checkboxInnerDone]}>
              {step.done && <Text style={styles.checkmark}>✓</Text>}
            </View>
          </Pressable>

          <Text
            style={[styles.stepText, step.done && styles.stepTextDone]}
            numberOfLines={1}
          >
            {step.title}
          </Text>

          {step.title && (
            <View style={styles.chevronHint}>
              <Text style={styles.chevronText}>›</Text>
            </View>
          )}
        </View>
      ))}

      {/* Footer: Add step or Open link */}
      <Pressable style={[styles.footerRow, protocol.steps.length >= 6 && styles.footerRowBorder]} onPress={handleAddStep}>
        <Text style={styles.footerText}>＋ Add step</Text>
      </Pressable>

      {hasMoreSteps && (
        <Text style={styles.moreStepsHint}>{protocol.steps.length - 6} more step{protocol.steps.length - 6 !== 1 ? 's' : ''}</Text>
      )}
    </View>
  );
}

const useStyles = makeThemedStyles(({ t, radii }) =>
  StyleSheet.create({
  container: {
    overflow: 'hidden',
    boxShadow: `0 10px 30px ${t.glowSoft}`,
    backgroundColor: t.glass,
    borderWidth: 1,
    borderColor: t.navyLine,
    borderRadius: 22,
    minHeight: 300,
    maxHeight: 300,
    paddingVertical: 0,
    paddingHorizontal: 0,
    display: 'flex',
    flexDirection: 'column',
  },

  // Empty state
  emptyText: {
    flex: 1,
    textAlign: 'center',
    textAlignVertical: 'center',
    fontSize: 13,
    color: t.ivorySoft,
    fontStyle: 'italic',
    paddingHorizontal: 16,
  },
  openPill: {
    alignSelf: 'center',
    marginBottom: 12,
    backgroundColor: t.gold,
    borderRadius: radii.pill,
    paddingVertical: 6,
    paddingHorizontal: 12,
  },
  openPillText: {
    fontSize: 11,
    fontWeight: 'bold',
    color: t.onGold,
  },

  // Header
  header: {
    backgroundColor: t.navyRaised,
    borderBottomWidth: 1,
    borderBottomColor: t.lineFaint,
    paddingVertical: 10,
    paddingHorizontal: 14,
  },
  headerText: {
    fontSize: 11,
    fontWeight: 'bold',
    color: t.goldBright,
    letterSpacing: 1.5,
  },

  // Step rows
  stepRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 14,
    gap: 10,
  },
  stepRowBorder: {
    borderBottomWidth: 1,
    borderBottomColor: t.lineFaint,
  },

  checkbox: {
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkboxInner: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: t.gold,
    backgroundColor: 'transparent',
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkboxInnerDone: {
    backgroundColor: t.gold,
  },
  checkmark: {
    fontSize: 12,
    fontWeight: 'bold',
    color: t.onGold,
  },

  stepText: {
    flex: 1,
    fontSize: 13,
    color: t.ivory,
    backgroundColor: 'transparent',
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 8,
  },
  stepTextDone: {
    color: t.ivorySoft,
    textDecorationLine: 'line-through',
  },

  chevronHint: {
    width: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  chevronText: {
    fontSize: 14,
    color: t.ivorySoft,
  },

  // Footer
  footerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    paddingHorizontal: 14,
  },
  footerRowBorder: {
    borderTopWidth: 1,
    borderTopColor: t.lineFaint,
  },
  footerText: {
    fontSize: 12,
    color: t.ivorySoft,
    fontWeight: '500',
  },

  moreStepsHint: {
    fontSize: 9,
    color: t.ivorySoft,
    textAlign: 'center',
    paddingBottom: 8,
    fontStyle: 'italic',
  },
  }),
);
